import PropTypes from "prop-types";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import get from "lodash/get";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { useDebounce } from "hooks/useDebounce";

import ListInput from "./ListInput";
import { REQUIRED_MARK } from "./fieldClasses";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * A chip list that also suggests existing values as the user types. Values
 * not suggested can still be entered freely, as in `FormChipList`.
 * `fetchSuggestions(search)` resolves to an array of strings.
 */
const FormSuggestChipList = ({
  name,
  label,
  required,
  placeholder,
  disabled,
  id,
  fetchSuggestions,
  suggestionsKey,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;

  const [draft, setDraft] = useState("");
  const search = useDebounce(draft.trim(), SEARCH_DEBOUNCE_MS);

  const { data: suggestions = [] } = useQuery({
    queryKey: ["chip-suggestions", suggestionsKey ?? name, search],
    queryFn: () => fetchSuggestions(search),
    enabled: search !== "",
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const chips = Array.isArray(field.value) ? field.value : [];
        const available = draft.trim()
          ? suggestions.filter(suggestion => !chips.includes(suggestion))
          : [];

        const addSuggestion = suggestion => {
          field.onChange([...chips, suggestion]);
          setDraft("");
        };

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <ListInput
              id={inputId}
              value={chips}
              onChange={field.onChange}
              draft={draft}
              onDraftChange={setDraft}
              error={!!error}
              required={required}
              disabled={disabled}
              placeholder={placeholder}
            />
            {available.length > 0 ? (
              <div
                role="group"
                aria-label="Suggestions"
                className="tw:flex tw:flex-wrap tw:items-center tw:gap-1"
              >
                <FieldDescription>Suggestions:</FieldDescription>
                {available.map(suggestion => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="outline"
                    size="xs"
                    disabled={disabled}
                    // Keeps the input focused, so its blur does not commit
                    // the partial draft as a chip before the click lands.
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => addSuggestion(suggestion)}
                  >
                    <PlusIcon data-icon="inline-start" />
                    {suggestion}
                  </Button>
                ))}
              </div>
            ) : null}
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormSuggestChipList.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  fetchSuggestions: PropTypes.func.isRequired,
  suggestionsKey: PropTypes.string,
};

export default FormSuggestChipList;
